// HPO (Human Phenotype Ontology) Search Utilities

// TypeScript interfaces for HPO data structure
export interface HPOSynonym {
  pred: string
  val: string
  synonymType?: string
}

export interface HPODefinition {
  val: string
  xrefs?: string[]
}

export interface HPOMeta {
  definition?: HPODefinition
  comments?: string[]
  synonyms?: HPOSynonym[]
  xrefs?: Array<{ val: string }>
  basicPropertyValues?: Array<{ pred: string; val: string }>
}

export interface HPONode {
  id: string
  lbl: string
  type: string
  meta?: HPOMeta
}

export interface HPOGraph {
  id: string
  meta: Record<string, unknown>
  nodes: HPONode[]
}

export interface HPOData {
  graphs: HPOGraph[]
}

export interface HPOSearchResult {
  node: HPONode
  matchType: 'label' | 'synonym'
  matchedText: string
  score: number
}

export interface HPOSearchOptions {
  caseSensitive?: boolean
  limit?: number
  minScore?: number
  searchInLabels?: boolean
  searchInSynonyms?: boolean
}

export class HPOSearchService {
  private hpoData: HPOData | null = null

  /**
   * Extract HPO ID from URL format
   * Converts "http://purl.obolibrary.org/obo/HP_0000001" to "HP:0000001"
   * @param urlId - The full URL ID from HPO data
   * @returns The clean HPO ID format
   */
  extractHPOId(urlId: string): string {
    if (!urlId) return urlId

    // Extract the HP_XXXXXXXX part from the URL
    const match = urlId.match(/HP_\d+$/)
    if (match) {
      // Convert HP_XXXXXXXX to HP:XXXXXXXX format
      return match[0].replace('_', ':')
    }

    // If it's already in HP:XXXXXXXX format, return as is
    if (urlId.startsWith('HP:')) {
      return urlId
    }

    // If no match found, return original
    return urlId
  }

  /**
   * Load HPO data from the JSON file
   * This should be called once when the module is initialized
   */
  async loadHPOData(): Promise<HPOData> {
    if (this.hpoData) {
      return this.hpoData
    }

    try {
      // Import the JSON file directly
      const hpoDataModule = await import('./hp.json')
      this.hpoData = hpoDataModule.default as HPOData
      return this.hpoData
    } catch (error) {
      console.error('Failed to load HPO data:', error)
      throw new Error('Could not load HPO data')
    }
  }

  /**
   * Search HPO terms based on substring matching in labels and synonyms
   * @param query - The search query string
   * @param options - Search configuration options
   * @returns Array of matching HPO terms with relevance scores
   */
  searchHPO(query: string, options: HPOSearchOptions = {}): HPOSearchResult[] {
    if (!this.hpoData) {
      throw new Error('HPO data not loaded. Call loadHPOData() first.')
    }

    const { caseSensitive = false, limit = 50, minScore = 0, searchInLabels = true, searchInSynonyms = true } = options

    const searchQuery = caseSensitive ? query : query.toLowerCase()
    const results: HPOSearchResult[] = []

    // Get all nodes from the first graph
    const nodes = this.hpoData.graphs[0]?.nodes || []

    for (const node of nodes) {
      const matches: HPOSearchResult[] = []

      // Search in labels
      if (searchInLabels && node.lbl) {
        const labelText = caseSensitive ? node.lbl : node.lbl.toLowerCase()
        if (labelText.includes(searchQuery)) {
          const score = this.calculateRelevanceScore(query, node.lbl, 'label')
          matches.push({
            node,
            matchType: 'label',
            matchedText: node.lbl,
            score,
          })
        }
      }

      // Search in synonyms
      if (searchInSynonyms && node.meta?.synonyms) {
        for (const synonym of node.meta.synonyms) {
          if (synonym.val) {
            const synonymText = caseSensitive ? synonym.val : synonym.val.toLowerCase()
            if (synonymText.includes(searchQuery)) {
              const score = this.calculateRelevanceScore(query, synonym.val, 'synonym')
              matches.push({
                node,
                matchType: 'synonym',
                matchedText: synonym.val,
                score,
              })
            }
          }
        }
      }

      // Add the best match for this node
      if (matches.length > 0) {
        const bestMatch = matches.reduce((best, current) => (current.score > best.score ? current : best))
        results.push(bestMatch)
      }
    }

    // Sort by relevance score (highest first) and apply limits
    return results
      .filter((result) => result.score >= minScore)
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
  }

  /**
   * Calculate relevance score for a match
   * Higher scores indicate better matches
   */
  private calculateRelevanceScore(query: string, text: string, matchType: 'label' | 'synonym'): number {
    const queryLower = query.toLowerCase()
    const textLower = text.toLowerCase()

    let score = 0

    // Exact match gets highest score
    if (textLower === queryLower) {
      score += 100
    }
    // Starts with query gets high score
    else if (textLower.startsWith(queryLower)) {
      score += 80
    }
    // Contains query as whole word gets medium-high score
    else if (new RegExp(`\\b${queryLower}\\b`).test(textLower)) {
      score += 60
    }
    // Contains query as substring gets medium score
    else if (textLower.includes(queryLower)) {
      score += 40
    }

    // Label matches get slight preference over synonyms
    if (matchType === 'label') {
      score += 5
    }

    // Shorter matches get slight preference (more specific)
    const lengthPenalty = Math.min(text.length / 100, 10)
    score -= lengthPenalty

    return Math.max(score, 0)
  }

  /**
   * Get HPO term by ID
   * @param id - The HPO ID (e.g., "HP:0000001" or "http://purl.obolibrary.org/obo/HP_0000001")
   * @returns The HPO node or null if not found
   */
  getHPOTermById(id: string): HPONode | null {
    if (!this.hpoData) {
      throw new Error('HPO data not loaded. Call loadHPOData() first.')
    }

    const nodes = this.hpoData.graphs[0]?.nodes || []

    // First try exact match
    let node = nodes.find((node) => node.id === id)
    if (node) return node

    // If ID is in clean format (HP:0000001), convert to URL format for search
    if (id.startsWith('HP:')) {
      const urlId = `http://purl.obolibrary.org/obo/${id.replace(':', '_')}`
      node = nodes.find((node) => node.id === urlId)
      if (node) return node
    }

    // If ID is in URL format, try to find by ending
    if (id.includes('HP_')) {
      node = nodes.find((node) => node.id.endsWith(id.split('/').pop() || ''))
      if (node) return node
    }

    return null
  }

  /**
   * Get all synonyms for a given HPO term
   * @param node - The HPO node
   * @returns Array of synonym strings
   */
  getHPOSynonyms(node: HPONode): string[] {
    if (!node.meta?.synonyms) {
      return []
    }

    return node.meta.synonyms.map((synonym) => synonym.val).filter((val) => val && val.trim().length > 0)
  }

  /**
   * Get the definition for a given HPO term
   * @param node - The HPO node
   * @returns The definition string or null if not available
   */
  getHPODefinition(node: HPONode): string | null {
    return node.meta?.definition?.val || null
  }

  /**
   * Search with autocomplete functionality
   * Returns results as the user types, with debouncing
   */
  createHPOAutocomplete(onResults: (results: HPOSearchResult[]) => void, debounceMs: number = 300) {
    let timeoutId: NodeJS.Timeout

    return (query: string, options?: HPOSearchOptions) => {
      clearTimeout(timeoutId)

      timeoutId = setTimeout(() => {
        try {
          const results = this.searchHPO(query, { ...options, limit: 10 })
          onResults(results)
        } catch (error) {
          console.error('HPO search error:', error)
          onResults([])
        }
      }, debounceMs)
    }
  }
}
